/**
 * Cross-tab communication composable using BroadcastChannel API
 * @param {string} channelName - The broadcast channel name
 * @returns {Object} Broadcast functions for pageload, signout, and auth handling
 * 
 * BroadcastChannel API Docs: https://developer.mozilla.org/en-US/docs/Web/API/Broadcast_Channel_API
 */
export default function useBroadcastApi(channelName) {
    // BroadcastChannel API is browser-only - return placeholder functions for server-side execution
    if (!process.client || typeof BroadcastChannel === 'undefined') {
        return {
            broadcastPageload: () => {},
            broadcastSignout: () => {},
            handleAuthBroadcast: () => {}
        }
    }

    const channel = new BroadcastChannel(channelName)
    let isListenerSetup = false

    const broadcastPageload = () => {
        channel.postMessage('pageload')
    }

    const broadcastSignout = () => {
        channel.postMessage('signout')
    }

    const handleAuthBroadcast = (profile) => {
        // Prevent duplicate listeners
        if (isListenerSetup) return
        isListenerSetup = true
        
        const isAuth = useCookie('isAuth')

        channel.onmessage = (event) => {
            const { data } = event
    
            if (data === 'signout') {
                // Handle signout broadcast - redirect listening tabs to logout page
                window.location.pathname = '/signin/logout'
            } else if (data === 'pageload' && isAuth.value !== profile?.isAuthenticated) {
                // Handle pageload broadcast - reload tab if auth state is out of sync
                // This ensures all tabs reflect the current auth status (i.e. after login/logout happens in another tab)
                window.location.reload()
            }
        }
    }

    // Disconnect from the broadcast channel when component unmounts
    onUnmounted(() => channel.close())

    return { broadcastPageload, broadcastSignout, handleAuthBroadcast }    

}